// src/components/EdenPlannerDraftReview.js
//
// Presentational review card for an Eden-generated Sherloc Media Planner draft.
// It never mutates planner state on its own — the parent owns apply/dismiss.
// Eden can draft fields only; it cannot launch, deploy, save, or spend.

import React, { useMemo, useState } from "react";

function fieldList(items, renderItem) {
  if (!items || !items.length) return null;
  return <ul className="smp-eden-draft-list">{items.map(renderItem)}</ul>;
}

export default function EdenPlannerDraftReview({
  result,
  appliedNote = "",
  computePreview,
  onApply,
  onDismiss,
}) {
  const draft = result?.draft || {};
  const assumptions = useMemo(
    () => (Array.isArray(result?.assumptions) ? result.assumptions : []),
    [result]
  );
  const missingInformation = useMemo(
    () => (Array.isArray(result?.missingInformation) ? result.missingInformation : []),
    [result]
  );
  const confidence = result?.confidence || "low";

  // Per-field overwrite confirmations for fields that already hold a value.
  const [overwriteFields, setOverwriteFields] = useState([]);

  // Preview what would happen against the current form (read-only).
  const preview = useMemo(
    () => (typeof computePreview === "function" ? computePreview(overwriteFields) : null),
    [computePreview, overwriteFields]
  );

  if (!result || !draft || Object.keys(draft).length === 0) return null;

  const applied = preview?.applied || [];
  const conflicts = preview?.conflicts || [];

  const toggleOverwrite = (field) => {
    setOverwriteFields((prev) =>
      prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
    );
  };

  return (
    <div className="smp-eden-draft-card" role="region" aria-label="Eden planner draft">
      <div className="smp-eden-draft-header">
        <span className="badge-pill section-pill">Eden Draft</span>
        <span className="smp-eden-draft-confidence">Confidence: {confidence}</span>
      </div>

      <p className="smp-eden-draft-note">
        Eden structured your intake into a draft. Review and apply when ready — nothing is
        saved, launched, or spent automatically.
      </p>

      {applied.length ? (
        <div className="smp-eden-draft-section">
          <div className="smp-eden-draft-subtitle">Ready to fill</div>
          {fieldList(applied, (item) => (
            <li key={item.formKey} className="smp-eden-draft-item">
              <strong>{item.label}:</strong> {String(item.value)}
            </li>
          ))}
        </div>
      ) : null}

      {conflicts.length ? (
        <div className="smp-eden-draft-section">
          <div className="smp-eden-draft-subtitle">Already filled — confirm to overwrite</div>
          <ul className="smp-eden-draft-list">
            {conflicts.map((conflict) => (
              <li key={conflict.formKey} className="smp-eden-draft-item">
                <label className="smp-eden-draft-overwrite">
                  <input
                    type="checkbox"
                    checked={overwriteFields.includes(conflict.field)}
                    onChange={() => toggleOverwrite(conflict.field)}
                  />
                  <span>
                    <strong>{conflict.label}:</strong> keep “{String(conflict.currentValue)}” or use
                    Eden’s “{String(conflict.suggestedValue)}”
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {assumptions.length ? (
        <div className="smp-eden-draft-section">
          <div className="smp-eden-draft-subtitle">Assumptions to confirm</div>
          {fieldList(assumptions, (item, index) => (
            <li key={index} className="smp-eden-draft-item">
              {item}
            </li>
          ))}
        </div>
      ) : null}

      {missingInformation.length ? (
        <div className="smp-eden-draft-section">
          <div className="smp-eden-draft-subtitle">Still needs your input</div>
          {fieldList(missingInformation, (item, index) => (
            <li key={index} className="smp-eden-draft-item">
              {item}
            </li>
          ))}
        </div>
      ) : null}

      {appliedNote ? <div className="smp-eden-draft-applied">{appliedNote}</div> : null}

      <div className="smp-eden-draft-actions">
        <button
          type="button"
          className="mediaplanner-save-btn"
          onClick={() => onApply?.({ overwriteFields })}
        >
          Apply to Planner
        </button>
        <button type="button" className="smp-eden-draft-dismiss" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
}
